import { api } from '@/lib/api'
import { API_PATHS } from '@/constants/api-paths'
import type {
  ApiResponse,
  ApplyWorkflowStatusPayload,
  JobCancellationReason,
  JobCancellationReasonPayload,
  JobCancellationReasonEntityType,
  JobKind,
  JobKindChildRule,
  JobKindChildRulePayload,
  JobKindPayload,
  JobStage,
  JobStageDefinition,
  JobStageDefinitionPayload,
  JobStagePayload,
  JobStageTransition,
  JobStageTransitionActor,
  JobStageTransitionActorPayload,
  JobStageTransitionPayload,
  JobWorkflowStatus,
  JobWorkflowStatusDefinition,
  JobWorkflowStatusDefinitionPayload,
  JobWorkflowStatusChange,
  JobWorkflowStatusPayload,
  JobWorkflowTransition,
  JobWorkflowTransitionActor,
  JobWorkflowTransitionActorPayload,
  JobWorkflowTransitionPayload,
  PaginatedResponse,
} from '@/types'

export type WorkflowActorOption = {
  value: string
  label: string
  scope?: string | null
}

export type WorkflowActorOptions = {
  roles: WorkflowActorOption[]
  posts: WorkflowActorOption[]
  users: WorkflowActorOption[]
  worker_skills: WorkflowActorOption[]
}

type ListResponse<T> = ApiResponse<PaginatedResponse<T>> | PaginatedResponse<T> | T[]

type CrudPaths = {
  list: string
  create: string
  detail: (id: string | number) => string
  update: (id: string | number) => string
  delete: (id: string | number) => string
}

export type JobWorkflowKind = 'job' | 'project' | 'event'

const normalizeList = <T>(payload: ListResponse<T>): PaginatedResponse<T> => {
  const inner = 'data' in payload ? payload.data : payload
  if (Array.isArray(inner)) {
    return { count: inner.length, next: null, previous: null, results: inner }
  }
  return inner
}

const list = async <T>(path: string, params?: Record<string, unknown>): Promise<PaginatedResponse<T>> => {
  const response = await api.get<ListResponse<T>>(path, { params })
  return normalizeList(response.data)
}

const resolveKindedListPath = (paths: { list: string; projectList?: string; eventList?: string }, kind: JobWorkflowKind): string => {
  if (kind === 'project' && paths.projectList) {
    return paths.projectList
  }
  if (kind === 'event' && paths.eventList) {
    return paths.eventList
  }
  return paths.list
}

const listByKind = async <T>(
  paths: { list: string; projectList?: string; eventList?: string },
  params: Record<string, unknown> | undefined,
  kind: JobWorkflowKind,
): Promise<PaginatedResponse<T>> => {
  const scopedPath = resolveKindedListPath(paths, kind)
  // Dedicated Project/Event list routes already force kind server-side.
  // Avoid sending kind=<code> which can be misread as a FK id on some backends.
  const usesDedicatedPath = scopedPath !== paths.list
  return list<T>(scopedPath, usesDedicatedPath ? { ...(params ?? {}) } : { ...(params ?? {}), kind })
}

const create = async <T, P>(path: string, payload: P): Promise<T> => {
  const response = await api.post<ApiResponse<T>>(path, payload)
  return response.data.data
}

const unwrapData = <T>(payload: ApiResponse<T> | T): T => {
  if (typeof payload === 'object' && payload !== null && 'data' in payload) {
    return (payload as ApiResponse<T>).data
  }
  return payload as T
}

const update = async <T, P>(path: string, payload: P): Promise<T> => {
  const response = await api.post<ApiResponse<T>>(path, payload)
  return response.data.data
}

const remove = async (path: string): Promise<void> => {
  await api.delete(path)
}

const crud = <T, P>(paths: CrudPaths) => ({
  list: (params?: Record<string, unknown>) => list<T>(paths.list, params),
  create: (payload: P) => create<T, P>(paths.create, payload),
  update: (id: string | number, payload: Partial<P>) => update<T, Partial<P>>(paths.update(id), payload),
  delete: (id: string | number) => remove(paths.delete(id)),
})

export const jobWorkflowApi = {
  kinds: crud<JobKind, JobKindPayload>(API_PATHS.jobWorkflow.kinds),
  childRules: crud<JobKindChildRule, JobKindChildRulePayload>(API_PATHS.jobWorkflow.childRules),
  statusDefinitions: crud<JobWorkflowStatusDefinition, JobWorkflowStatusDefinitionPayload>(API_PATHS.jobWorkflow.statusDefinitions),
  statuses: crud<JobWorkflowStatus, JobWorkflowStatusPayload>(API_PATHS.jobWorkflow.statuses),
  transitions: crud<JobWorkflowTransition, JobWorkflowTransitionPayload>(API_PATHS.jobWorkflow.transitions),
  transitionActors: crud<JobWorkflowTransitionActor, JobWorkflowTransitionActorPayload>(API_PATHS.jobWorkflow.transitionActors),
  cancellationReasons: crud<JobCancellationReason, JobCancellationReasonPayload>(API_PATHS.jobWorkflow.cancellationReasons),
  stageDefinitions: crud<JobStageDefinition, JobStageDefinitionPayload>(API_PATHS.jobWorkflow.stageDefinitions),
  stages: crud<JobStage, JobStagePayload>(API_PATHS.jobWorkflow.stages),
  stageTransitions: crud<JobStageTransition, JobStageTransitionPayload>(API_PATHS.jobWorkflow.stageTransitions),
  stageTransitionActors: crud<JobStageTransitionActor, JobStageTransitionActorPayload>(API_PATHS.jobWorkflow.stageTransitionActors),
  listStatusesByKind: (kind: JobWorkflowKind, params?: Record<string, unknown>) => listByKind<JobWorkflowStatus>(API_PATHS.jobWorkflow.statuses, params, kind),
  listTransitionsByKind: (kind: JobWorkflowKind, params?: Record<string, unknown>) => listByKind<JobWorkflowTransition>(API_PATHS.jobWorkflow.transitions, params, kind),
  listTransitionActorsByKind: (kind: JobWorkflowKind, params?: Record<string, unknown>) => listByKind<JobWorkflowTransitionActor>(API_PATHS.jobWorkflow.transitionActors, params, kind),
  listStagesByKind: (kind: JobWorkflowKind, params?: Record<string, unknown>) => listByKind<JobStage>(API_PATHS.jobWorkflow.stages, params, kind),
  listStageTransitionsByKind: (kind: JobWorkflowKind, params?: Record<string, unknown>) => listByKind<JobStageTransition>(API_PATHS.jobWorkflow.stageTransitions, params, kind),
  listStageTransitionActorsByKind: (kind: JobWorkflowKind, params?: Record<string, unknown>) => listByKind<JobStageTransitionActor>(API_PATHS.jobWorkflow.stageTransitionActors, params, kind),
  runtime: {
    getStatusOptions: async (entityType: string, entityId: string | number): Promise<JobWorkflowStatus[]> => {
      const response = await api.get<ApiResponse<JobWorkflowStatus[]> | JobWorkflowStatus[]>(API_PATHS.jobWorkflow.runtime.statusOptions, {
        params: { entity_type: entityType, entity_id: entityId },
      })
      return unwrapData(response.data) ?? []
    },
    getStatusTransitions: async (entityType: string, entityId: string | number): Promise<JobWorkflowTransition[]> => {
      const response = await api.get<ApiResponse<JobWorkflowTransition[]> | JobWorkflowTransition[]>(API_PATHS.jobWorkflow.runtime.transitions, {
        params: { entity_type: entityType, entity_id: entityId },
      })
      return unwrapData(response.data) ?? []
    },
    getCancellationReasons: async (
      entityType: JobCancellationReasonEntityType,
      options?: { entity_id?: string | number; service_instance_id?: string | number },
    ): Promise<JobCancellationReason[]> => {
      const response = await api.get<ApiResponse<JobCancellationReason[]> | JobCancellationReason[]>(API_PATHS.jobWorkflow.runtime.cancellationReasons, {
        params: {
          entity_type: entityType,
          entity_id: options?.entity_id,
          service_instance_id: options?.service_instance_id,
        },
      })
      return unwrapData(response.data) ?? []
    },
    applyStatus: async (payload: ApplyWorkflowStatusPayload): Promise<JobWorkflowStatusChange> => {
      const response = await api.post<ApiResponse<JobWorkflowStatusChange> | JobWorkflowStatusChange>(API_PATHS.jobWorkflow.runtime.applyStatus, payload)
      return unwrapData(response.data)
    },
    getActorOptions: async (): Promise<WorkflowActorOptions> => {
      const response = await api.get<ApiResponse<WorkflowActorOptions> | WorkflowActorOptions>(API_PATHS.jobWorkflow.runtime.actorOptions)
      return unwrapData(response.data) ?? { roles: [], posts: [], users: [], worker_skills: [] }
    },
  },
}

export default jobWorkflowApi
